import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ProductCategory {
  id: string;
  name: string;
  description: string;
  image_url: string;
  slug: string;
}

const FALLBACK_CATEGORIES: ProductCategory[] = [
  {
    id: 'free-resources',
    name: 'Free Resources',
    slug: 'free-resources',
    description: 'Helpful tools & resources for digital download',
    image_url:
      'https://blooma-images.s3.us-east-2.amazonaws.com/shop/shop_categories/free-resources.jpg',
  },
  {
    id: 'garden-guides',
    name: 'Garden Guides',
    slug: 'garden-guides',
    description: "Our best selling garden plant guides for anything you're growing",
    image_url:
      'https://blooma-images.s3.us-east-2.amazonaws.com/shop/shop_categories/garden-guides.jpg',
  },
  {
    id: 'tools-and-equipment',
    name: 'Tools & Equipment',
    slug: 'tools-and-equipment',
    description: 'Essential gardening tools and equipment',
    image_url:
      'https://blooma-images.s3.us-east-2.amazonaws.com/shop/shop_categories/tools-and-supplies.jpg',
  },
];

export default function Features() {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('product_categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories(FALLBACK_CATEGORIES);
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  return (
    <div className="py-4 bg-white shop-section">
      <div className="container mx-auto px-4">
        {/* Shop Categories */}
        <div>
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-bold">Garden Resources</h2>
            <Link 
              to="/shop" 
              className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
            >
              View All Resources
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
              {categories.map(category => (
                <Link 
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative h-40 md:h-64 overflow-hidden rounded-lg"
                >
                  <img 
                    src={category.image_url} 
                    alt={category.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-4 md:p-6">
                    <h3 className="text-white text-lg md:text-xl font-semibold">{category.name}</h3>
                    {category.description && (
                      <p className="text-gray-200 text-sm mt-1 line-clamp-2">{category.description}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}